import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, Sparkles, Coffee, ShoppingBag, Megaphone, Scissors, Wrench,
  HeartPulse, GraduationCap, Car, Building2, Store, Briefcase, Package, Home, Plane
} from "lucide-react";
import { INDUSTRIES } from "../data/industries";
import { SEO } from "../components/SEO";

const ICONS = {
  Coffee, ShoppingBag, Megaphone, Scissors, Wrench,
  HeartPulse, GraduationCap, Car, Building2, Store,
  Briefcase, Package, Home, Plane
};

// Pull problem/solution preview from the industry data
const buildPreview = (ind) => ({
  problem: ind.challenges[0]?.title || "",
  solution: ind.strategy[0]?.title || ""
});

const IndustriesPage = () => {
  const navigate = useNavigate();

  return (
    <div className="pt-20" data-testid="industries-page">
      <SEO
        title="Industries"
        description="SmartReach™ industry solutions — restaurants, retail, entertainment, salons, healthcare, automotive, education, and multi-location brands. Built for local foot traffic."
        path="/industries"
      />

      <section className="section-padding bg-gradient-to-br from-orange-light via-white to-white">
        <div className="max-w-7xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 text-orange-primary font-semibold text-sm mb-4 bg-white px-4 py-2 rounded-full shadow-sm">
            <Sparkles size={16} /> Industry Solutions
          </span>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-gray-dark mb-6 leading-tight">
            Built For The Way <span className="text-gradient-orange">Local Businesses Grow</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-medium mb-8 max-w-3xl mx-auto">
            Every industry has its own foot-traffic playbook. SmartReach&trade; adapts to yours — with the
            channels, targeting, and incentives that actually move customers in your category.
          </p>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {INDUSTRIES.map((ind) => {
              const Icon = ICONS[ind.iconName] || Store;
              const preview = buildPreview(ind);
              return (
                <Link
                  key={ind.slug}
                  to={`/industries/${ind.slug}`}
                  className="bg-white p-6 rounded-2xl shadow-card border border-gray-100 card-hover block group"
                  data-testid={`industry-card-${ind.slug}`}
                >
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${ind.accent} flex items-center justify-center mb-4`}>
                    <Icon className="text-white" size={24} />
                  </div>
                  <h3 className="font-display font-bold text-xl text-gray-dark mb-2">{ind.name}</h3>
                  <p className="text-gray-medium text-sm mb-4">{ind.tagline}</p>
                  <div className="space-y-3 pt-3 border-t border-gray-100">
                    <div>
                      <p className="text-xs uppercase tracking-wide font-semibold text-gray-medium mb-1">Key Problem</p>
                      <p className="text-sm text-gray-dark">{preview.problem}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide font-semibold text-orange-primary mb-1">SmartReach Solution</p>
                      <p className="text-sm text-gray-dark">{preview.solution}</p>
                    </div>
                  </div>
                  <span className="text-orange-primary font-semibold text-sm inline-flex items-center gap-1 mt-4 group-hover:gap-2 transition-all">
                    See industry strategy <ArrowRight size={14} />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Industry Comparison Table */}
      <section className="section-padding bg-gray-light" data-testid="industry-comparison-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-3">
              Industry <span className="text-gradient-orange">Comparison</span>
            </h2>
            <p className="text-gray-medium max-w-2xl mx-auto text-lg">
              At-a-glance — top challenge, lead strategy, and a flagship outcome metric per industry.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left" data-testid="industry-comparison-table">
                <thead className="bg-gradient-to-r from-orange-primary to-orange-secondary text-white">
                  <tr>
                    <th className="px-4 py-4 font-display font-semibold text-sm">Industry</th>
                    <th className="px-4 py-4 font-display font-semibold text-sm hidden sm:table-cell">Top Challenge</th>
                    <th className="px-4 py-4 font-display font-semibold text-sm">Lead Strategy</th>
                    <th className="px-4 py-4 font-display font-semibold text-sm">Flagship KPI</th>
                    <th className="px-4 py-4 font-display font-semibold text-sm" />
                  </tr>
                </thead>
                <tbody>
                  {INDUSTRIES.map((ind, idx) => {
                    const Icon = ICONS[ind.iconName] || Store;
                    const flagshipKpi = ind.kpis[0];
                    return (
                      <tr
                        key={ind.slug}
                        className={`${idx % 2 === 0 ? "bg-white" : "bg-gray-50"} hover:bg-orange-light/30 transition-colors`}
                        data-testid={`comparison-row-${ind.slug}`}
                      >
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${ind.accent} flex items-center justify-center flex-shrink-0`}>
                              <Icon className="text-white" size={16} />
                            </div>
                            <span className="font-display font-semibold text-gray-dark text-sm">{ind.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-medium hidden sm:table-cell">
                          {ind.challenges[0]?.title}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-dark">
                          {ind.strategy[0]?.title}
                        </td>
                        <td className="px-4 py-4">
                          <div>
                            <span className="font-display font-bold text-orange-primary text-base">{flagshipKpi.val}</span>
                            <p className="text-xs text-gray-medium">{flagshipKpi.label}</p>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <Link
                            to={`/industries/${ind.slug}`}
                            className="text-orange-primary font-semibold text-sm inline-flex items-center gap-1 hover:gap-2 transition-all whitespace-nowrap"
                          >
                            View <ArrowRight size={14} />
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <p className="text-center text-xs text-gray-medium/70 mt-4 italic">
            * KPI ranges are illustrative benchmarks. Your results will be unique to your business.
          </p>
        </div>
      </section>

      <section className="section-padding gradient-orange">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">
            Don't see your industry? We can still help.
          </h2>
          <p className="text-white/90 text-lg mb-8">
            If your business depends on local customers, SmartReach&trade; can move the needle.
          </p>
          <button onClick={() => navigate("/contact")} className="bg-white text-orange-primary px-8 py-4 rounded-full font-semibold text-lg hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
            Book a Demo <ArrowRight size={20} className="inline ml-2" />
          </button>
        </div>
      </section>
    </div>
  );
};

export default IndustriesPage;
